import { StateCreator } from 'zustand'
import {
  logger,
  enqueueLoadedSnackbar,
  exceptionMessageHandler,
  singletonQueryClient,
  getCompanyId,
} from '$utils'
import { SharedSlice } from '../../../../../slice'
import {
  IPayClassificationTimelineRecordRequest,
  PayClassification,
} from '$models'
import { payClassificationService } from '$services'

const invalidatePayClassificationDetail = (
  payClassificationId: string
): Promise<void> =>
  singletonQueryClient.invalidateQueries({
    queryKey: [
      payClassificationService.PAY_CLASSIFICATION_QUERY_KEYS.detail,
      getCompanyId(),
      payClassificationId,
    ],
  })

/**
 * Merges a mutation response over the loaded classification, keeping the held activation unless the
 * response carries one. These endpoints describe the classification itself and do not write the
 * per-venue activation, so a response can come back without one. Applying it wholesale would drop
 * the activation the loader fetched and the page would read the venue as deactivated - which for a
 * Custom classification also locks the form until a reload.
 */
const withPreservedActivation = (
  response: PayClassification,
  loaded: PayClassification | null
): PayClassification => ({
  ...response,
  activation: response.activation ?? loaded?.activation,
})

interface EditPayClassificationState {
  payClassification: PayClassification | null
}

export interface EditPayClassificationSlice extends EditPayClassificationState {
  /**
   * Reset PayClassification Slice
   */
  resetEditPayClassificationSlice: () => void

  /**
   * Get Pay Classification By id
   * @param payClassificationId PayClassification id
   */
  getPayClassification: (
    payClassificationId: string
  ) => Promise<PayClassification | null>

  /**
   * Update PayClassification
   * @param payClassification PayClassification to update
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   * @returns true when the update was persisted, false when it failed - callers that chain further
   * work off a save (or clear a dirty form) must not proceed on false
   */
  updatePayClassification: (
    payClassification: PayClassification,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<boolean>

  /**
   * Promote a Custom PayClassification to Managed
   * @param payClassificationId PayClassification to promote
   * @param successMessage Success Message to display if the promotion was successful
   * @param errorMessage Error Message to display if the promotion has failed
   * @returns true when the promotion was persisted, false when it failed
   */
  promotePayClassification: (
    payClassificationId: string,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<boolean>

  /**
   * Append a new timeline record (schedule a rate change) and refetch the parent classification.
   */
  createTimelineRecord: (
    payClassificationId: string,
    request: IPayClassificationTimelineRecordRequest
  ) => Promise<PayClassification | null>

  /**
   * Update an existing timeline record and refetch the parent classification.
   */
  updateTimelineRecord: (
    payClassificationId: string,
    recordId: string,
    request: IPayClassificationTimelineRecordRequest
  ) => Promise<PayClassification | null>

  /**
   * Soft-delete a timeline record and refetch the parent classification.
   */
  deleteTimelineRecord: (
    payClassificationId: string,
    recordId: string
  ) => Promise<PayClassification | null>
}

const initialState: EditPayClassificationState = {
  payClassification: null,
}

const createEditPayClassificationSlice: StateCreator<
  EditPayClassificationSlice & SharedSlice,
  [],
  [],
  EditPayClassificationSlice
> = (set, get) => ({
  // State variables
  ...initialState,

  // State setters
  resetEditPayClassificationSlice: () => set(initialState),

  // State API calls
  getPayClassification: async (
    payClassificationId: string
  ): Promise<PayClassification | null> => {
    try {
      const response = await payClassificationService.getPayClassification(
        payClassificationId
      )

      set(() => ({
        payClassification: response,
      }))

      return response
    } catch (error) {
      logger.error(
        `Failed to retrieve pay classification ${payClassificationId}`,
        error
      )
      set(() => ({
        payClassification: null,
      }))

      exceptionMessageHandler(error, 'Failed to retrieve pay classification')
      return null
    }
  },

  updatePayClassification: async (
    payClassification: PayClassification,
    successMessage?: string,
    errorMessage?: string
  ): Promise<boolean> => {
    try {
      set(() => ({ isLoading: true }))

      const updatedPayClassification =
        await payClassificationService.updatePayClassification(
          payClassification
        )

      // A null response means nothing was persisted (the service short-circuits when the venue
      // lacks the pay classification permissions), so the failure has to be detected before the
      // success toast is enqueued - enqueueing it first is what surfaced a success *and* an error
      // toast for the same failed save. The loaded classification is left in place as well, so a
      // rejected save no longer empties the store out from under the edit page.
      if (!updatedPayClassification) {
        logger.error(`${errorMessage} - updatedPayClassification doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return false
      }

      set((state) => ({
        payClassification: withPreservedActivation(
          updatedPayClassification,
          state.payClassification
        ),
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
      return true
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
      return false
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  promotePayClassification: async (
    payClassificationId: string,
    successMessage?: string,
    errorMessage?: string
  ): Promise<boolean> => {
    try {
      set(() => ({ isLoading: true }))

      const promotedPayClassification =
        await payClassificationService.promotePayClassification(
          payClassificationId
        )

      // As with a save, a null response means nothing was persisted (the service short-circuits
      // when the venue lacks the pay classification permissions) rather than a rejection, so the
      // failure has to be detected before the success toast is enqueued.
      if (!promotedPayClassification) {
        logger.error(
          `${errorMessage} - promotedPayClassification doesn't exist`
        )
        enqueueLoadedSnackbar(errorMessage, 'error')
        return false
      }

      set((state) => ({
        payClassification: withPreservedActivation(
          promotedPayClassification,
          state.payClassification
        ),
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
      return true
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
      return false
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  createTimelineRecord: async (
    payClassificationId: string,
    request: IPayClassificationTimelineRecordRequest
  ): Promise<PayClassification | null> => {
    try {
      await payClassificationService.createTimelineRecord(
        payClassificationId,
        request
      )
      await invalidatePayClassificationDetail(payClassificationId)
      const refreshed = await get().getPayClassification(payClassificationId)
      // getPayClassification shows its own error snackbar and returns null on
      // failure; only celebrate success once the refresh actually succeeded.
      if (refreshed) {
        enqueueLoadedSnackbar('Rate change scheduled', 'success')
      }
      return refreshed
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to schedule rate change')
      return null
    }
  },

  updateTimelineRecord: async (
    payClassificationId: string,
    recordId: string,
    request: IPayClassificationTimelineRecordRequest
  ): Promise<PayClassification | null> => {
    try {
      await payClassificationService.updateTimelineRecord(
        payClassificationId,
        recordId,
        request
      )
      await invalidatePayClassificationDetail(payClassificationId)
      const refreshed = await get().getPayClassification(payClassificationId)
      if (refreshed) {
        enqueueLoadedSnackbar('Rate record updated', 'success')
      }
      return refreshed
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to update rate record')
      return null
    }
  },

  deleteTimelineRecord: async (
    payClassificationId: string,
    recordId: string
  ): Promise<PayClassification | null> => {
    try {
      await payClassificationService.deleteTimelineRecord(
        payClassificationId,
        recordId
      )
      await invalidatePayClassificationDetail(payClassificationId)
      const refreshed = await get().getPayClassification(payClassificationId)
      if (refreshed) {
        enqueueLoadedSnackbar('Rate record deleted', 'success')
      }
      return refreshed
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to delete rate record')
      return null
    }
  },
})

export default createEditPayClassificationSlice
